/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useState, Ref } from 'preact/hooks';
import { isKeyDefined } from '../../utils/UNSAFE_keys';
import { getElementContainsFunc } from '../../utils/PRIVATE_collectionUtils';

/**
 * Hook for handling Tabbable mode set provider components.
 *
 * @param rootRef - a Ref to the root element of the consuming component
 * @param keyExtractor - function to extract the key based on provided element
 * @param currentKey - current key navigation "cursor" is on
 * @param onCurrentKeyChange - optional callback to notify when the currentKey should change
 * @param keySupportsTabbable - optional callback to prevent tabbable mode from being entered on non-tabbable keys
 * @param keyComparator - optional callback to compare the equality of two keys
 * @returns isCurrentTabbableKey - callback for parent to check if key is in interactive mode
 * @returns tabbableSetProps - event handlers mode relies on
 * @returns getCurrentTabbableKey - callback to get the current tabbable key
 * @returns setCurrentTabbableKey - callback to set the current tabbable key
 */
export function useTabbableModeSet<K>(
  rootRef: Ref<HTMLElement>,
  keyExtractor: (element: HTMLElement) => K | null,
  currentKey?: K,
  onCurrentKeyChange?: ({ value }: { value: K }) => void,
  keySupportsTabbable?: (key: K) => boolean,
  keyComparator?: (key1?: K, key2?: K) => boolean
): [
  (key?: K) => boolean,
  {
    onFocus: (event: FocusEvent) => void;
    onBlur: (event: FocusEvent) => void;
    onKeyDown: (event: KeyboardEvent) => void;
  },
  () => K | undefined,
  (key?: K) => void
] {
  // Track which row is in actionable in state
  const [currentTabbableKey, setCurrentTabbableKey] = useState<K>();

  const keyEquals = useCallback(
    (key1?: K, key2?: K) => {
      if (keyComparator) {
        return keyComparator(key1, key2);
      }
      return key1 === key2;
    },
    [keyComparator]
  );

  const disableTabbableMode = useCallback(
    (target: EventTarget | null, isKeyboardEvent = false) => {
      setCurrentTabbableKey(undefined);
      // handle clean-up on disabling of the mode
      if (getElementContainsFunc(rootRef.current, true)(target as Element) && isKeyboardEvent) {
        rootRef.current?.focus();
      }
    },
    [rootRef, setCurrentTabbableKey]
  );
  const enableTabbableMode = useCallback(
    (key: K) => {
      setCurrentTabbableKey(key);
      if (onCurrentKeyChange && !keyEquals(key, currentKey)) {
        onCurrentKeyChange({ value: key });
      }
    },
    [currentKey, keyEquals, onCurrentKeyChange, setCurrentTabbableKey]
  );
  const isCurrentTabbableKey = useCallback(
    (key?: K) => {
      return keyEquals(key as K, currentTabbableKey as K);
    },
    [currentTabbableKey, keyEquals]
  );
  const getCurrentTabbableKey = useCallback(() => {
    return currentTabbableKey;
  }, [currentTabbableKey]);

  const onFocus = (event: FocusEvent) => {
    // if the root is focused while tabbable, exit tabbable mode
    if (event.target === rootRef.current) {
      if (!isCurrentTabbableKey(undefined)) {
        disableTabbableMode(event.target);
      }
    } else {
      const key = keyExtractor(event.target as HTMLElement) as K;
      if (isKeyDefined(key) && !isCurrentTabbableKey(key)) {
        enableTabbableMode(key);
      }
    }
  };

  const onBlur = (event: FocusEvent) => {
    if (
      !event.relatedTarget ||
      !getElementContainsFunc(rootRef.current, true)(event.relatedTarget as Element)
    ) {
      disableTabbableMode(event.relatedTarget);
    }
  };

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (currentKey != null && (keySupportsTabbable == null || keySupportsTabbable(currentKey))) {
        if (event.key === 'F2') {
          isCurrentTabbableKey(currentKey)
            ? disableTabbableMode(event.target, true)
            : enableTabbableMode(currentKey);
        } else if (event.key === 'Esc' || event.key === 'Escape') {
          disableTabbableMode(event.target, true);
        }
      }
    },
    [currentKey, disableTabbableMode, enableTabbableMode, keySupportsTabbable, isCurrentTabbableKey]
  );

  const tabbableSetProps = {
    onFocus,
    onBlur,
    onKeyDown
  };

  return [isCurrentTabbableKey, tabbableSetProps, getCurrentTabbableKey, setCurrentTabbableKey];
}
